import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import '../../../styles/components/destination_page/overview/ImageBlock.css';

const ImageBlock = ({ imageData = {}, summary, defaultSrc, loading = false }) => {
  const {
    source,
    url,
    alt_description,
    photographer_name,
    photographer_profile_url,
    source_homepage_url
  } = imageData;

  const [status, setStatus] = useState('pending');
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!url) {
      setStatus('pending');
      return;
    }

    setStatus('pending');

    const img = new Image();
    img.onload = () => {
      setImgSrc(url);
      setStatus('loaded');
    };
    img.onerror = () => {
      setImgSrc(defaultSrc);
      setStatus('error');
    };
    img.src = url;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url, defaultSrc]);

  if (loading || status === 'pending') {
    return <div className="image-skeleton" role="status" aria-busy="true" />;
  }

  const altText = alt_description || summary;
  const provider =
    source && typeof source === 'string'
      ? source.charAt(0).toUpperCase() + source.slice(1)
      : null;

  return (
    <div className="overview-image-block">
      <img
        className="overview-image"
        src={imgSrc}
        alt={altText}
        loading="lazy"
        width="1280"
        height="720"
      />

      <div className="overview-overlay">
        {photographer_name && source_homepage_url && (
          <span className="overview-attribution">
            Photo&nbsp;by&nbsp;
            <a
              href={DOMPurify.sanitize(photographer_profile_url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {DOMPurify.sanitize(photographer_name)}
            </a>{' '}
            on&nbsp;
            <a
              href={DOMPurify.sanitize(source_homepage_url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {provider}
            </a>
          </span>
        )}
        <p className="overview-summary">{summary}</p>
      </div>
    </div>
  );
};

ImageBlock.propTypes = {
  imageData: PropTypes.shape({
    source: PropTypes.string,
    url: PropTypes.string,
    alt_description: PropTypes.string,
    photographer_name: PropTypes.string,
    photographer_profile_url: PropTypes.string,
    source_homepage_url: PropTypes.string
  }),
  summary: PropTypes.string.isRequired,
  defaultSrc: PropTypes.string.isRequired,
  loading: PropTypes.bool
};

export default ImageBlock;
